import React, { useState, useMemo } from 'react';
import { getCoreBranches, sumBranchPoints, maxBranchPoints } from './heroClasses';
import SkillDetailModal from './SkillDetailModal';
import { BRANCHES } from './data';
import './SkillTreeVisualization.css';

// Helper to get all leaf skills from a node - DEFENSIVE VERSION
function getLeafIds(node) {
  // Ensure node exists and has valid structure
  if (!node || typeof node !== 'object') return [];

  // Check if node has children - must be an array
  const children = Array.isArray(node.children) ? node.children : null;

  // If no children or empty children, return this node's id
  if (!children || children.length === 0) {
    return node.id ? [node.id] : [];
  }

  // Recursively get all leaf ids from children
  const leafIds = [];
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child) {
      const childLeaves = getLeafIds(child);
      if (Array.isArray(childLeaves)) {
        leafIds.push(...childLeaves);
      }
    }
  }
  return leafIds;
}

/**
 * SkillTreeVisualization - Detailed hierarchical skill tree
 * Shows only subgroups that are under progress or fully completed
 */
function SkillTreeVisualization({ ratings = {}, isReadOnly = true }) {
  const [hoveredSkillId, setHoveredSkillId] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showNodeModal, setShowNodeModal] = useState(false);

  // Helper function to find a node in BRANCHES tree - DEFENSIVE VERSION
  const findNodeInTree = (nodeId) => {
    if (!nodeId || typeof nodeId !== 'string') return null;
    if (!BRANCHES || !Array.isArray(BRANCHES)) return null;

    for (let i = 0; i < BRANCHES.length; i++) {
      const branch = BRANCHES[i];
      if (branch) {
        const found = findNodeInBranch(nodeId, branch);
        if (found) return found;
      }
    }
    return null;
  };

  const findNodeInBranch = (nodeId, node) => {
    // Safety checks
    if (!node || typeof node !== 'object') return null;
    if (!nodeId || typeof nodeId !== 'string') return null;

    // Check if this node matches
    if (node.id === nodeId) return node;

    // Recursively search children
    const childrenArray = Array.isArray(node.children) ? node.children : null;
    if (childrenArray && childrenArray.length > 0) {
      for (let i = 0; i < childrenArray.length; i++) {
        const child = childrenArray[i];
        if (child) {
          const found = findNodeInBranch(nodeId, child);
          if (found) return found;
        }
      }
    }
    return null;
  };

  // Get branch color for a skill ID
  const getBranchColorForSkill = (skillId) => {
    if (skillId && skillId.includes('tech-def')) return '#22c55e'; // Defense - Green
    if (skillId && skillId.includes('tech-att')) return '#ef4444'; // Attack - Red
    if (skillId && skillId.includes('tact-')) return '#38bdf8'; // Tactics - Blue
    if (skillId && skillId.includes('brawn-')) return '#fb923c'; // Brawn - Orange
    return '#d4a574'; // Default gold
  };

  // Handle opening skill detail modal
  const handleNodeTap = (nodeId) => {
    setSelectedNodeId(nodeId);
    setShowNodeModal(true);
  };

  // Handle closing skill detail modal
  const handleCloseModal = () => {
    setShowNodeModal(false);
    setTimeout(() => setSelectedNodeId(null), 300);
  };

  // Get branch data with structure
  const branchData = useMemo(() => {
    const coreBranches = getCoreBranches();
    return [
      {
        id: 'defense',
        name: 'Defense',
        branch: coreBranches.defense,
        color: '#22c55e',
        rgb: '34, 197, 94',
      },
      {
        id: 'attack',
        name: 'Attack',
        branch: coreBranches.attack,
        color: '#ef4444',
        rgb: '239, 68, 68',
      },
      {
        id: 'tactics',
        name: 'Tactics',
        branch: coreBranches.tactics,
        color: '#38bdf8',
        rgb: '56, 189, 248',
      },
      {
        id: 'brawn',
        name: 'Brawn',
        branch: coreBranches.brawn,
        color: '#fb923c',
        rgb: '251, 146, 60',
      },
    ];
  }, []);

  // Build detailed skill tree with hierarchy and progress info - DEFENSIVE VERSION
  const skillHierarchy = useMemo(() => {
    const hierarchies = {};

    if (!branchData || !Array.isArray(branchData)) {
      return hierarchies;
    }

    branchData.forEach((branch) => {
      const buildHierarchy = (node, level = 0) => {
        // Safety checks for node validity
        if (!node || typeof node !== 'object') return null;
        if (!node.id || typeof node.id !== 'string') return null;

        // Safely get leaf IDs
        const leafIds = Array.isArray(getLeafIds(node)) ? getLeafIds(node) : [];

        // Safely map ratings
        const childRatings = leafIds.length > 0
          ? leafIds.map(id => ratings && ratings[id] ? ratings[id] : 0)
          : [];

        // Safely count with defensive checks
        const completedCount = childRatings.length > 0
          ? childRatings.filter(r => typeof r === 'number' && r >= (node.maxRating || 3)).length
          : 0;

        const startedCount = childRatings.length > 0
          ? childRatings.filter(r => typeof r === 'number' && r > 0).length
          : 0;

        const totalLeaves = leafIds.length;

        // Check if this is a leaf node
        const isLeaf = !node.children || (Array.isArray(node.children) && node.children.length === 0);

        // Safely calculate current rating
        const currentRating = isLeaf
          ? (ratings && ratings[node.id] ? ratings[node.id] : 0)
          : (childRatings.length > 0 ? Math.max(...childRatings.filter(r => typeof r === 'number'), 0) : 0);

        const maxRating = typeof node.maxRating === 'number' ? node.maxRating : 3;
        const status = currentRating >= maxRating ? 'completed' : (currentRating > 0 ? 'progress' : 'locked');

        // Build children array safely
        const childrenArray = Array.isArray(node.children) ? node.children : [];
        const builtChildren = childrenArray
          .map(child => buildHierarchy(child, level + 1))
          .filter(child => child !== null && child !== undefined);

        const nodeInfo = {
          id: node.id,
          title: node.title || 'Untitled',
          level,
          isLeaf,
          currentRating,
          maxRating,
          status,
          children: builtChildren,
          startedCount,
          completedCount,
          totalLeaves,
        };

        return nodeInfo;
      };

      if (branch && branch.id && branch.branch) {
        hierarchies[branch.id] = buildHierarchy(branch.branch);
      }
    });

    return hierarchies;
  }, [branchData, ratings]);

  // Filter to show all nodes in the tree (no filtering by status)
  const filterVisibleNodes = (node) => {
    // Ensure node is valid object
    if (!node || typeof node !== 'object') return null;

    // For parent nodes, recursively include all children
    const childrenArray = Array.isArray(node.children) ? node.children : [];
    const allChildren = childrenArray
      .map(child => filterVisibleNodes(child))
      .filter(child => child !== null && child !== undefined);

    // Return the node with all its children (show everything)
    return {
      ...node,
      children: allChildren,
    };
  };

  const visibleHierarchy = useMemo(() => {
    const filtered = {};

    if (!skillHierarchy || typeof skillHierarchy !== 'object') {
      return filtered;
    }

    Object.entries(skillHierarchy).forEach(([branchId, hierarchy]) => {
      if (!branchId || !hierarchy) return;

      const filtered_hierarchy = filterVisibleNodes(hierarchy);

      // Only add if filtered hierarchy exists and has visible children
      if (
        filtered_hierarchy &&
        typeof filtered_hierarchy === 'object' &&
        Array.isArray(filtered_hierarchy.children) &&
        filtered_hierarchy.children.length > 0
      ) {
        filtered[branchId] = filtered_hierarchy;
      }
    });

    return filtered;
  }, [skillHierarchy]);

  // Render a detailed skill node - DEFENSIVE VERSION
  const renderDetailedNode = (node, branchColor, level = 0) => {
    // Safety checks for node validity
    if (!node || typeof node !== 'object') return null;
    if (!node.id) return null;
    if (!branchColor || typeof branchColor !== 'string') branchColor = '#d4a574';

    // Safely check for children
    const childrenArray = Array.isArray(node.children) ? node.children : [];
    const hasChildren = childrenArray.length > 0;
    const isHovered = hoveredSkillId === node.id;
    const isExpanded = expandedGroups && expandedGroups.has ? expandedGroups.has(node.id) : false;

    // Safely check numeric values
    const currentRating = typeof node.currentRating === 'number' ? node.currentRating : 0;
    const maxRating = typeof node.maxRating === 'number' ? node.maxRating : 3;
    const completedCount = typeof node.completedCount === 'number' ? node.completedCount : 0;
    const totalLeaves = typeof node.totalLeaves === 'number' ? node.totalLeaves : 0;

    // Only show mastered if ALL children are mastered (for parents) or node is mastered (for leaves)
    const isMastered = hasChildren
      ? completedCount === totalLeaves && totalLeaves > 0
      : currentRating === maxRating && maxRating > 0;

    const isUnderProgress = node.status === 'progress';

    // Dark-theme defaults (the app forces a dark background everywhere -
    // these used to be light-theme leftovers: #f8f8f8/#ddd/#333, which is
    // unreadable dark-on-dark once the branchColor tint fades to transparent)
    const baseBgColor = 'rgba(255, 255, 255, 0.04)';
    let bgColor = baseBgColor;
    let borderColor = 'rgba(255, 255, 255, 0.15)';
    // Text colour comes from the stylesheet (dark text on the light panel, light text on the dark one)
    let textColor;

    if (isMastered) {
      bgColor = `${branchColor}15`;
      borderColor = branchColor;
    } else if (isUnderProgress) {
      bgColor = `${branchColor}10`;
      borderColor = branchColor;
    }

    const progressPercent = maxRating > 0 ? (currentRating / maxRating) * 100 : 0;
    const nodeTitle = node.title || 'Untitled Skill';

    // Leaf cards fill left-to-right with the same tint bgColor already
    // resolves to (mastered/in-progress/locked), proportional to
    // currentRating/maxRating - e.g. a 1/3-rated leaf shows that tint
    // across the left third of the card and the plain baseBgColor for the
    // rest, so "1/3" reads visually as well as textually. At 0/3 and at
    // maxRating this is pixel-identical to the plain flat bgColor already
    // used below (0% or 100% of a two-stop gradient is just one color),
    // so nothing about the existing 0/3 or fully-mastered look changes -
    // only the partially-filled states gain the new proportional fill.
    // Parent/group nodes are untouched - they already get their own
    // dedicated progress bar (st-node-progress below) and keep the flat
    // backgroundColor they always had.
    const leafBackground = !hasChildren
      ? `linear-gradient(to right, ${bgColor} 0%, ${bgColor} ${progressPercent}%, ${baseBgColor} ${progressPercent}%, ${baseBgColor} 100%)`
      : null;

    return (
      <div key={node.id}>
        <div
          className="st-detailed-node"
          style={{
            ...(hasChildren ? { backgroundColor: bgColor } : { background: leafBackground }),
            borderColor: borderColor,
            borderLeft: `${4 + level * 3}px solid ${branchColor}`,
            marginLeft: `${level * 8}px`,
            cursor: hasChildren ? 'pointer' : 'default',
            transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
            transition: 'all 0.2s ease',
          }}
          onClick={() => {
            if (hasChildren) {
              setExpandedGroups(prev => {
                const newSet = new Set(prev);
                if (newSet.has(node.id)) {
                  newSet.delete(node.id);
                } else {
                  newSet.add(node.id);
                }
                return newSet;
              });
            } else {
              handleNodeTap(node.id);
            }
          }}
          onMouseEnter={() => setHoveredSkillId(node.id)}
          onMouseLeave={() => setHoveredSkillId(null)}
        >
          <div className="st-node-header">
            <div className="st-node-title-section">
              {hasChildren && (
                <span className="st-node-expand-icon" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                  ➤
                </span>
              )}
              <span className="st-node-title" style={{ color: textColor }}>
                {nodeTitle}
              </span>
            </div>

            <div className="st-node-rating" style={{ color: branchColor }}>
              {currentRating}/{maxRating}
            </div>
          </div>

          {/* Progress bar for parent nodes */}
          {hasChildren && totalLeaves > 0 && (
            <div className="st-node-progress">
              <div className="st-node-progress-bar-bg">
                <div
                  className="st-node-progress-bar-fill"
                  style={{
                    width: `${progressPercent}%`,
                    backgroundColor: branchColor,
                  }}
                />
              </div>
              <div className="st-node-progress-text">
                {completedCount}/{totalLeaves} completed
              </div>
            </div>
          )}

          {/* Status badge - only show if truly mastered */}
          {isMastered && node.status === 'completed' && (
            <div className="st-node-status" style={{ color: branchColor }}>
              ✓ Mastered
            </div>
          )}
        </div>

        {/* Render children if expanded and they exist */}
        {isExpanded && hasChildren && Array.isArray(childrenArray) && childrenArray.length > 0 && (
          <div className="st-node-children">
            {childrenArray.map((child, idx) => {
              if (!child) return null;
              return (
                <React.Fragment key={`${node.id}-child-${idx}`}>
                  {renderDetailedNode(child, branchColor, level + 1)}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="st-skill-tree-visualization-detailed">
      <div className="st-tree-header">
        <h2 className="st-tree-title">Skill Progression</h2>
        <p className="st-tree-subtitle">Your mastery path across all disciplines</p>
      </div>

      <div className="st-tree-branches">
        {branchData && Array.isArray(branchData) && branchData.map((branch) => {
          // Validate branch object
          if (!branch || typeof branch !== 'object') return null;

          const hierarchy = visibleHierarchy && visibleHierarchy[branch.id];

          // Only show branch if it has visible progress
          if (!hierarchy || typeof hierarchy !== 'object') {
            return null;
          }

          // Safely extract stats with defaults
          const startedCount = typeof hierarchy.startedCount === 'number' ? hierarchy.startedCount : 0;
          const totalLeaves = typeof hierarchy.totalLeaves === 'number' ? hierarchy.totalLeaves : 0;
          const branchColor = branch.color || '#d4a574';
          const branchName = branch.name || 'Unknown';
          const branchRgb = branch.rgb || '212, 165, 116';

          return (
            <div key={branch.id} className="st-branch-section">
              <div
                className="st-branch-header"
                style={{
                  borderColor: branchColor,
                  backgroundColor: `rgba(${branchRgb}, 0.05)`,
                }}
              >
                <h3 className="st-branch-title" style={{ color: branchColor }}>
                  {branchName}
                </h3>
                <div className="st-branch-stats" style={{ color: branchColor }}>
                  {(() => {
                    // Calculate branch points to match stats tab
                    const branchCurrentPoints = branch.branch ? sumBranchPoints(branch.branch, ratings) : 0;
                    const branchMaxPoints = branch.branch ? maxBranchPoints(branch.branch) : 0;
                    return `${branchCurrentPoints} / ${branchMaxPoints} pts`;
                  })()}
                </div>
              </div>

              <div className="st-branch-content">
                {renderDetailedNode(hierarchy, branchColor)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Skill Detail Modal with branch color - DEFENSIVE VERSION */}
      {selectedNodeId && showNodeModal && (
        <SkillDetailModal
          isOpen={showNodeModal}
          skill={findNodeInTree(selectedNodeId)}
          currentRating={ratings && ratings[selectedNodeId] ? ratings[selectedNodeId] : 0}
          onClose={handleCloseModal}
          readOnly={true}
          branchColor={getBranchColorForSkill(selectedNodeId)}
          breadcrumbPath={[]}
        />
      )}
    </div>
  );
}

export default SkillTreeVisualization;
